<template>
  <div class="explorer-view">
    <div class="message-bar">
      <MessageBarV02
        color="#223448"
        dark
        dense
        dismissible
        transition="fade-transition"
        tile
      >
        {{ interactionTimeMessage }}
        <br>
        <strong>{{ avgTimeSpentDeprecationMessage }}</strong>
      </MessageBarV02>
    </div>
    <ExplorerHeader
      :reports="allReports"
      :resultSet="resultSetDownload"
      :isReportRunnable="isValidReportRequest"
      :isUpdated="isReportUpdated"
      :activeFilter="showReportParams"
      @downloadClick="downloadData"
      @reportSelect="setReportOptions"
      @updateReport="runExplorerReport"
      @toggleTimeSeries="toggleTimeSeriesChart"
      @toggleFilters="showReportParams = !showReportParams"
      @toggleSaveReportPopUp="showSaveReportPopUp = !showSaveReportPopUp"
      @toggleAlreadyScheduledReportPopUp=
        "showAlreadyScheduledReportPopUp = !showAlreadyScheduledReportPopUp"
      @openScheduleReportSlideOut="showScheduleReportSlideOut = true"
    ></ExplorerHeader>
    <ExplorerFiltersDisplay
      v-if="showReportParams"
      :dateValue="dateValue"
      :minDate="minDate"
      :maxDate="maxDate"
      :dimensions="selectedDimensionsObjects"
      :metrics="selectedMetricsObjects"
      :filters="selectedFilters"
      :portfolioBrands="portfolioBrands"
      @add="handleFilterAdd"
      @edit="handleFilterAdd"
      @dismiss="handleParameterDismiss"
      @clearReport="clearCurrentDashboardReportId"
      @dateApply="handleDateApply"
    />
    <SaveReportPopUp
      :show="showSaveReportPopUp"
      @toggleSaveReportPopUp="showSaveReportPopUp = !showSaveReportPopUp"
      @saveReportOnly="saveAndScheduleReport"
      @saveAndSchedule="initScheduleReportSlideOut"
    ></SaveReportPopUp>
    <AlreadyScheduledReportPopUp
      :show="showAlreadyScheduledReportPopUp"
      @toggleAlreadyScheduledReportPopUp=
        "showAlreadyScheduledReportPopUp = !showAlreadyScheduledReportPopUp"
    ></AlreadyScheduledReportPopUp>
    <ExplorerFiltersHeader
      v-if="!showReportParams"
      class="filters-header"
      :dateValue="dateValue"
      :filters="selectedFilters"
      :minDate="minDate"
      :maxDate="maxDate"
      :portfolioBrands="portfolioBrands"
      @add="handleFiltersHeaderClick"
      @dateApply="handleDateApply"
      @clearReport="clearCurrentDashboardReportId"
    />
    <ExplorerSlideOut
      ref="slideOut"
      :value="selectedReportFields"
      :show="showSlideOut"
      :dimensions="availableDimensions"
      :metrics="availableMetrics"
      :portfolioBrands="portfolioBrands"
      @cancel="showSlideOut = false"
      @complete="applyFilters"
    ></ExplorerSlideOut>
    <ScheduleReportSlideOut
      :show="showScheduleReportSlideOut"
      :customReportName="scheduleReportName"
      :isEditing="false"
      :dataWindowStart="startDateStr"
      :dataWindowEnd="endDateStr"
      @saveAndScheduleReport="saveAndScheduleReport"
      @sendPreview="sendPreview"
      @cancel="showScheduleReportSlideOut = false"
    ></ScheduleReportSlideOut>
    <transition name="fade-container">
      <div v-if="showExplorerTimeSeriesChart">
        <ExplorerChartCard
          :dataKey="selectedYAxis"
          :data="tableRows"
          :loading="explorerDataPending"
          :message="lineChartMessage"
        >
          <template slot="metric-dropdown">
            <Select
              v-if="selectedYAxis.value"
              id="time-series-metric-select"
              name="chart-metric-select"
              label="Y Axis Metric"
              :value="selectedYAxis.value"
              :options="selectedMetricsObjects"
              @change="updateYAxis"
            ></Select>
          </template>
        </ExplorerChartCard>
      </div>
    </transition>
    <div class="explore-table" :class="{ active: hasValidDataToDisplay || explorerDataPending }">
      <Loader
        :loading="explorerDataPending"
        :error="dashboardError"
        :message="''"
      >
        <div class="table explorer">
          <div class="explorer-table-box">
            <Table
              v-if="hasValidDataToDisplay"
              :data="tableRows"
              :summaryRow="summaryRow"
              :customColumns="tableColumns"
              :fixed-header="true"
              :disableFooterItemsPerPage="true"
              :height="'500px'"
              :items-per-page="200"
              :columnAlignment="'left'"
              :sort-by.sync="sortKey"
              :sort-desc.sync="sortDesc"
            ></Table>
          </div>
        </div>
      </Loader>
    </div>
    <OnboardingTour
      :tourSteps="steps"
      tourName="Explorer"
    ></OnboardingTour>
  </div>
</template>
<script>
import { Select, MessageBarV02 } from '@mikmak/components';
import { cloneDeep, isEqual } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { downloadTableData } from '../../../services/dashboard/downloadService';
import {
  getNormalizedMetricValues,
  getAvailableDimensions,
  getAvailableMetrics,
  getMetricsValuesByGroup,
} from '../../../services/dashboard/exclusionsService';
import {
  getReportFieldFromValue,
  getReportFieldSnakeFromValue,
  getReportFieldsFromValues,
  getUniqueGroups,
  getReportFieldFromSnake,
} from '../../../services/dashboard/reportFieldService';
import { getSelectedPortfolioBrands } from '../../../services/dashboard/portfolioBrandsService';
import { explorerTourSteps } from './config/onboardingTourSteps';
import preConfiguredReports from '../Views/config/preConfiguredReports';
import {
  createDashboardReport,
  getDashboardReport,
} from '../../../services/dashboard/dashboardReportService';
import { getDashboardExplorerData } from '../../../services/dashboard/eventApiService';
import {
  createScheduledReport,
  sendPreviewReport,
  isReportScheduled,
} from '../../../services/dashboard/scheduledReportService';
import eventDispatcher from '../../../events/dispatch';
import ExplorerFiltersDisplay from '../Explorer/ExplorerFiltersDisplay.vue';
import ExplorerFiltersHeader from '../Explorer/ExplorerFiltersHeader.vue';
import ExplorerSlideOut from '../Explorer/SlideOut/ExplorerSlideOut.vue';
import ExplorerHeader from '../Explorer/ExplorerHeader.vue';
import Loader from '../Common/Charting/Shared/Loader';
import OnboardingTour from './OnboardingTour';
import Table from '../Common/Table.vue';
import {
  getLocalDateFromUnixTimestamp,
  getNumberOfDays,
} from '../../../services/dashboard/dates/datesService';
import {
  getFilterSelectionsTotalCount,
  getErrorMessage,
} from '../../../services/dashboard/dashboardUtils';
import ExplorerChartCard from '../Explorer/ExplorerCard/ExplorerChartCard.vue';
import SaveReportPopUp
  from '../../../components/dashboard/Explorer/SavedReports/SaveReportPopUp.vue';
import AlreadyScheduledReportPopUp
  from '../../../components/dashboard/Explorer/SavedReports/AlreadyScheduledReportPopUp.vue';
import snackbarMixin from '../../../mixins/snackbar';
import ScheduleReportSlideOut from '../Common/ScheduleReport/ScheduleReport.vue';
import { format as formatDate } from 'date-fns';
import {
  getSelectedFilters,
  explorerAllowedFilters,
} from '../../../services/dashboard/filtersService';
import {
  rateDisplayCsvGetter,
  eventDateValueCsvGetter,
} from '../../../services/dashboard/displayFormatService';
import { dashboardMessages } from './config/dashboardMessages';

export default {
  mixins: [ snackbarMixin ],
  props: {
    minDate: Date,
    maxDate: Date,
    showExplorerTimeSeriesChart: Boolean,
    isComingFromInsightsCard: Boolean,
    dateRange: Object,
    dateValue: Object,
    startDate: Date,
    endDate: Date,
    startDateStr: String,
    endDateStr: String,
    isValidDateRange: Boolean,
    brandIds: Array,
    brandNames: Array,
    encodedBrandIds: String,
  },
  data() {
    return {
      sortKey: '',
      sortDesc: true,
      columnDisplayOrdering: null,
      granularity: 'DAY',
      preConfiguredReports,
      explorerDataPending: false,
      dashboardError: false,
      showReportParams: false,
      showSlideOut: false,
      isReportUpdated: false,
      viewName: 'Explorer',
      dashboardReportRetrievalErrorMessage: `Unable to access report.
        Please try again later.
        If the problem persists, please contact your Customer Success Manager.`,
      dashboardReportDoesNotExistErrorMessage: `The report you're trying to access does not exist.
        Please create a new report.`,
      steps: explorerTourSteps,
      selectedYAxis: {},
      showSaveReportPopUp: false,
      showAlreadyScheduledReportPopUp: false,
      showScheduleReportSlideOut: false,
      scheduleReportName: '',
      hint: '',
      interactionTimeMessage: dashboardMessages.interactionTimeNewMetric,
      avgTimeSpentDeprecationMessage: dashboardMessages.avgTimeSpentDeprecation,
    };
  },
  components: {
    ExplorerFiltersDisplay,
    ExplorerFiltersHeader,
    ExplorerSlideOut,
    ExplorerHeader,
    Loader,
    MessageBarV02,
    OnboardingTour,
    Select,
    ExplorerChartCard,
    SaveReportPopUp,
    AlreadyScheduledReportPopUp,
    ScheduleReportSlideOut,
    Table,
  },
  computed: {
    ...mapState({
      selectedDimensions: state => state.dashboard.reportRequest.dimensions,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      selectedMetrics: state => state.dashboard.reportRequest.metrics,
      portfolioBrands: state => state.dashboard.portfolioBrands,
      currentDashboardReportId: state => state.dashboard.currentDashboardReportId,
      currentUser: state => state.main.currentUser,
      explorerData: state => state.dashboard.explorerData,
      defaultDimensions: state => state.dashboard.availableReportFields.dimensions,
      defaultMetrics: state => state.dashboard.availableReportFields.metrics,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      userSavedReports: state => state.dashboard.userSavedReports,
      allowedFilters: state => state.filters.allowedFilters.explorer,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
      showNewExplorerMessages: state => state.main.showNewExplorerMessages,
    }),
    ...mapGetters([
      'selectedBrandIds',
    ]),
    selectedFilters() {
      return getSelectedFilters(
        this.dashboardSelectedFilters,
        this.allowedFilters,
      );
    },
    isValidReportRequest() {
      return this.isValidDateRange
        && this.brandIds
        && this.brandIds.length >= 1
        && this.selectedDimensionsObjects
        && this.selectedDimensionsObjects.length >= 1
        && this.selectedMetricsObjects
        && this.selectedMetricsObjects.length >= 1;
    },
    tableRows() {
      if (this.explorerData && this.explorerData.rows) {
        return this.explorerData.rows;
      }

      if (this.explorerData && this.explorerData.length) {
        return this.explorerData;
      }

      return [];
    },
    tableColumns() {
      if (!this.tableRows || !this.tableRows.length || !this.tableRows[0]) {
        return [];
      }

      const columnsObject = this.summaryRow || this.tableRows[0];

      const columns = Object.keys(columnsObject);

      if (!columns || !columns.length) {
        return [];
      }

      const useDisplayOrdering = this.columnDisplayOrdering
        && this.containsSameElements(this.columnDisplayOrdering, columns);

      return useDisplayOrdering ? this.columnDisplayOrdering : columns;
    },
    downloadedReportColumns() {
      return this.tableColumns.map(column => {
        const reportParameter = getReportFieldFromSnake(this.allReportFields, column);

        const isRateType = reportParameter && reportParameter.type === 'rate';
        const isEventDate = column === 'event_date';

        let columnValue = column;

        if (isRateType) columnValue = rateDisplayCsvGetter(column);
        else if (isEventDate) columnValue = eventDateValueCsvGetter('event_date');

        return {
          label: column,
          value: columnValue,
        };
      });
    },
    summaryRow() {
      return this.explorerData && this.explorerData.summary;
    },
    resultSetDownload() {
      return {
        rows: this.tableRows,
        columns: this.tableColumns,
      };
    },
    initialSort() {
      return this.explorerData && this.explorerData.initialSort;
    },
    hasValidDataToDisplay() {
      return this.tableColumns.length && this.tableRows.length && !this.dashboardError;
    },
    selectedReportFields() {
      return {
        dimensions: this.selectedDimensionsObjects,
        metrics: this.selectedMetricsObjects,
        filters: this.selectedFilters,
      };
    },
    selectedFilterDimensions() {
      return this.dashboardSelectedFilters.map(f => f.key);
    },
    selectedDimensionsObjects() {
      return getReportFieldsFromValues(this.allReportFields, this.selectedDimensions);
    },
    selectedMetricsObjects() {
      return getReportFieldsFromValues(this.allReportFields, this.selectedMetrics);
    },
    selectedFilterDimensionsObjects() {
      return getReportFieldsFromValues(this.allReportFields, this.selectedFilterDimensions);
    },
    currentUserEmail() {
      return this.currentUser && this.currentUser.email;
    },
    availableMetrics() {
      return getAvailableMetrics(
        this.defaultMetrics,
        this.selectedDimensionsObjects,
        this.selectedFilterDimensionsObjects,
        this.selectedMetricsObjects,
      );
    },
    availableDimensions() {
      return getAvailableDimensions(
        this.defaultDimensions,
        this.selectedDimensionsObjects,
        this.selectedFilterDimensionsObjects,
        this.selectedMetricsObjects,
      );
    },
    hasDateDimension() {
      return this.tableRows && this.tableRows[0] && this.tableRows[0].event_date;
    },
    isRetailerDimensionSelected() {
      return this.selectedDimensions.includes('cartName');
    },
    isVariantDimensionSelected() {
      return this.selectedDimensions.includes('variantName');
    },
    retailerOnlyMetricsValues() {
      return getMetricsValuesByGroup(this.defaultMetrics, 'retailer');
    },
    productOnlyMetricsValues() {
      return getMetricsValuesByGroup(this.defaultMetrics, 'product');
    },
    filterDimensions() {
      return this.selectedFilters.map(f => f.key);
    },
    stringifiedFilters() {
      return JSON.stringify(this.selectedFilters);
    },
    filterSelectionsTotalCount() {
      if (!this.selectedFilters) return 0;

      return getFilterSelectionsTotalCount(this.selectedFilters);
    },
    analyticsObj() {
      return {
        start_date: this.startDateStr,
        end_date: this.endDateStr,
        dashboard_view: this.viewName,
        brand_ids: this.brandIds.join(', '),
        brand_names: this.brandNames.join(', '),
        brand_count: this.brandIds.length,
        dimensions: this.selectedDimensions.join(', '),
        dimension_count: this.selectedDimensions.length,
        metrics: this.selectedMetrics.join(', '),
        metric_count: this.selectedMetrics.length,
        filters: this.stringifiedFilters,
        filter_count: this.selectedFilters.length,
        filter_selections_total_count: this.filterSelectionsTotalCount,
        filter_stringified_length: this.stringifiedFilters.length,
        filter_dimensions: this.filterDimensions.join(', '),
        filter_dimensions_count: this.filterDimensions.length,
      };
    },
    lineChartMessage() {
      if (this.selectedDimensions.length > 1) {
        return 'Time Series analysis with more than one dimension is coming soon!';
      } else if (!this.selectedYAxis.snake) {
        return 'Please select a Y Axis Metric from the dropdown above';
      } else if (!this.hasDateDimension) {
        return 'Please include Event Date in the report to see the Time Series Chart';
      } else if (!this.tableRows || this.tableRows.length === 0) {
        return 'No Data';
      } else {
        return '';
      }
    },
    allReports() {
      const reports = this.userSavedReports.map(r => {
        return {
          category: 'Saved Reports',
          key: r.name,
          value: r.filterJson,
        };
      });

      return [ ...reports, ...this.preConfiguredReports ];
    },
  },
  beforeMount() {
    this.$store.commit('updateActiveView', this.viewName);
  },
  async mounted() {
    this.showReportParams = true;

    const dashboardReportId = this.$route.params.dashboardReportId;

    if (dashboardReportId && dashboardReportId !== this.currentDashboardReportId) {
      await this.getAndSetDashboardReport(dashboardReportId);
    } else if (this.isComingFromInsightsCard) {
      this.runExplorerReport();
      this.$emit('resetIsComingFromInsightsCard');
    } else if (this.explorerData && this.currentDashboardReportId) {
      this.setExplorerData(this.explorerData);
      this.$router.push({
        name: 'explorer-id',
        params: { dashboardReportId: this.currentDashboardReportId },
      });
    }

    this.setSortKey(this.tableColumns);
  },
  methods: {
    initScheduleReportSlideOut({ reportName }) {
      this.showSaveReportPopUp = false;
      this.scheduleReportName = reportName;
      this.showScheduleReportSlideOut = true;
    },
    getScheduledReportData(schedulingData) {
      const reportFilters = {
        brandIds: this.encodedBrandIds,
        dimensions: this.selectedDimensions,
        metrics: this.selectedMetrics,
        dateRange: {
          startDate: this.startDateStr,
          endDate: this.endDateStr,
          granularity: this.granularity,
        },
        unique: this.isSelectedReportUnique,
        filters: this.selectedFilters,
      };

      const {
        reportName,
        emailFrequency,
        emailSubject,
        fileType,
        emailTemplateHtml,
        emailRecipients,
        aggregateDays,
      } = schedulingData;

      return {
        name: reportName,
        brand: this.brandNames.join(', '),
        schedule: emailFrequency || null,
        emailSubject: emailSubject || '',
        startDate: schedulingData.startDate && new Date(schedulingData.startDate).toISOString(),
        endDate: schedulingData.endDate && new Date(schedulingData.endDate).toISOString(),
        dateCreated: formatDate(new Date(), 'MM/dd/yyy'),
        fileType: fileType || 'csv',
        createdByEmail: this.currentUserEmail,
        filterJson: reportFilters,
        emailTemplateHtml: emailTemplateHtml || '',
        recipientEmails: emailRecipients || [],
        aggregateDays,
      };
    },
    async sendPreview(schedulingData) {
      const reportData = {
        timestamp: Date.now(),
        report: this.getScheduledReportData(schedulingData),
      };

      try {
        const token = await this.$auth.getTokenSilently();

        this.alertSnackbar(`Preview sent to ${reportData.report.recipientEmails[0]}`);

        await sendPreviewReport(reportData, token);
      } catch (e) {
        const errorMessage = this.getErrorMessage(e);

        throw errorMessage;
      }
    },
    async saveAndScheduleReport(schedulingData) {
      const reportData = this.getScheduledReportData(schedulingData);

      let scheduledReportId = '';
      let hasError = false;
      let errorMessage = '';

      try {
        const token = await this.$auth.getTokenSilently();

        const id = await createScheduledReport(reportData, token);

        scheduledReportId = id;

        this.showSaveReportPopUp = false;
        const snackBarMessage = this.getReportScheduledSnackbarMessage(reportData.name);

        this.alertSnackbar(snackBarMessage);

        this.showScheduleReportSlideOut = false;

        this.$store.dispatch('setUserSavedReports', token);
        this.$router.push({ name: 'saved-reports-base' });

        return scheduledReportId;
      } catch (e) {
        hasError = true;
        errorMessage = this.getErrorMessage(e);

        throw errorMessage;
      } finally {
        const eventType = isReportScheduled(reportData)
          ? 'dashboardReportScheduled'
          : 'dashboardReportSaved';

        eventDispatcher(eventType, {
          ...this.analyticsObj,
          scheduled_report_id: scheduledReportId,
          created_at: new Date(),
          has_error: hasError,
          error_message: errorMessage,
        });

        this.showScheduleReportSlideOut = false;
      }
    },
    getReportScheduledSnackbarMessage(reportName) {
      return `Report for ${reportName} scheduled.`
        + ` You can view this report under Saved Reports.`;
    },
    handleFilterAdd(label) {
      this.showSlideOut = true;

      this.$nextTick(() => {
        this.$refs.slideOut.goToTab(label);
      });
    },
    handleParameterDismiss({ label, parameter }) {
      switch (label) {
        case 'dimensions':
          this.updateDimensionsValue(parameter.value);
          break;
        case 'metrics':
          this.updateMetricsValue(parameter.value);
          break;
        case 'filters':
          this.updateFilters(parameter);
          break;
        default:
          return;
      }
    },
    handleFiltersHeaderClick() {
      this.showReportParams = true;
      this.showSlideOut = true;
    },
    toggleTimeSeriesChart() {
      this.$emit('showTimeSeriesUpdated', !this.showExplorerTimeSeriesChart);

      eventDispatcher('dashboardLinechartToggled', {
        ...this.analyticsObj,
        active: this.showExplorerTimeSeriesChart,
      });
    },
    updateDimensionsValue(val) {
      this.clearCurrentDashboardReportId();

      const dimensionsValues = [...this.selectedDimensions];
      const index = dimensionsValues.indexOf(val);

      if (index >= 0) {
        dimensionsValues.splice(index, 1);
      } else {
        dimensionsValues.push(val);
      }

      const dimensionsObjects = getReportFieldsFromValues(this.allReportFields, dimensionsValues);
      const dimensionsGroups = getUniqueGroups(dimensionsObjects);

      if (val === 'cartName' && !dimensionsGroups.includes('retailer')) {
        for (const retailerMetric of this.retailerOnlyMetricsValues) {
          this.removeMetricIfSelected(retailerMetric);
        }

        const selectedRetailerFilterDimensions = this.selectedFilters.filter(
          f => f.key === 'cartName',
        );

        for (const selectedFilter of selectedRetailerFilterDimensions) {
          this.updateFilters(selectedFilter);
        }
      }

      if (val === 'variantName') {
        for (const productMetric of this.productOnlyMetricsValues) {
          this.removeMetricIfSelected(productMetric);
        }

        const selectedProductFilterDimensions = this.selectedFilters.filter(
          f => f.key === 'variantName',
        );

        for (const selectedFilter of selectedProductFilterDimensions) {
          this.updateFilters(selectedFilter);
        }
      }

      eventDispatcher('dashboardDimensionsChanged', {
        dashboard_view: this.viewName,
        dimensions: dimensionsValues,
        dimension_count: dimensionsValues.length,
      });
      this.$store.commit('updateReportDimensions', dimensionsValues);
    },
    updateMetricsValue(val) {
      this.clearCurrentDashboardReportId();

      const metrics = [...this.selectedMetrics];
      const index = metrics.indexOf(val);

      if (index >= 0) {
        metrics.splice(index, 1);
      } else {
        metrics.push(val);
      }

      if (this.retailerOnlyMetricsValues.indexOf(val) >= 0 && !this.isRetailerDimensionSelected) {
        this.updateDimensionsValue('cartName');
      }

      if (this.productOnlyMetricsValues.indexOf(val) >= 0 && !this.isVariantDimensionSelected) {
        this.updateDimensionsValue('variantName');
      }

      eventDispatcher('dashboardMetricsChanged', {
        dashboard_view: this.viewName,
        metrics: metrics,
        metric_count: metrics.length,
      });
      this.$store.commit('updateReportMetrics', metrics);
      this.$store.commit('updateExplorerAllowedFilters', explorerAllowedFilters());
    },
    removeMetricIfSelected(metricValue) {
      if (this.selectedMetrics.indexOf(metricValue) >= 0) {
        this.updateMetricsValue(metricValue);
      }
    },
    handleDateApply(value) {
      const { predefinedRangeValue, range } = value;

      this.$store.commit('updatePredefinedRangeValue', predefinedRangeValue);

      this.$store.commit('updateReportEndDate', range.end);
      this.$store.commit('updateReportStartDate', range.start);

      this.$nextTick(() => {
        this.clearCurrentDashboardReportId();
        this.runExplorerReport();
      });
    },
    updateYAxis(metricValue) {
      this.selectedYAxis = getReportFieldFromValue(this.allReportFields, metricValue) || {};
    },
    async setReportOptions(report) {
      const { brandIds, dimensions, metrics, filters, key: reportName } = report.value;

      // If the report has brand Ids saved, we apply them,
      // otherwise we fall through to whatever brand is currently set on the page
      if (brandIds) {
        const filteredBrandIds = brandIds && JSON.parse(decodeURIComponent(brandIds));

        await this.updatePortfolioBrands(filteredBrandIds);
      }

      this.$store.commit('updateReportDimensions', dimensions);
      this.$store.commit('updateReportMetrics', metrics);
      this.$store.dispatch(
        'setDashboardSelectedFilters',
        filters,
      );
      this.$store.commit('updateExplorerAllowedFilters', explorerAllowedFilters());

      this.clearCurrentDashboardReportId();

      eventDispatcher('dashboardPreMadeReportSelected', {
        dashboard_view: this.viewName,
        brand_ids: this.brandIds.join(', '),
        brand_names: this.brandNames.join(', '),
        report: reportName,
      });

      this.runExplorerReport();
    },
    downloadData() {
      const rowData = cloneDeep(this.tableRows);

      let downloadError = false;
      let fileName = undefined;
      let fileSize = undefined;

      try {
        const downloadObject = downloadTableData(this.downloadedReportColumns, rowData);

        fileName = downloadObject.name;
        fileSize = downloadObject.size;
      } catch (err) {
        this.emitError('csvDownloadError');

        downloadError = true;
      } finally {
        eventDispatcher('dashboardCSVDownloaded', {
          ...this.analyticsObj,
          file_name: fileName,
          file_size: fileSize,
          table_name: this.title,
          download_error: downloadError,
        });
      }
    },
    async applyFilters(newReportFields) {
      this.showSlideOut = false;
      this.isReportUpdated = true;

      const { dimensions, metrics, filters } = newReportFields;
      const dimensionValues = this.arrayIfEmpty(dimensions).map(d => d.value);
      const metricValues = this.arrayIfEmpty(metrics).map(m => m.value);
      const nonBrandFilters = this.arrayIfEmpty(filters).filter(f => f.key !== 'brandName');

      this.$store.commit('updateReportDimensions', dimensionValues);
      this.$store.commit('updateReportMetrics', metricValues);
      this.$store.dispatch(
        'setDashboardSelectedFilters',
        nonBrandFilters,
      );
      this.$store.commit('updateExplorerAllowedFilters', explorerAllowedFilters());

      const filteredBrandIds = filters.reduce((brandNames, { key, value }) => {
        return key === 'brandName' && value && value.length
          ? brandNames.concat(value)
          : brandNames;
      }, []);

      await this.updatePortfolioBrands(filteredBrandIds);

      this.runExplorerReport();
    },
    async updatePortfolioBrands(brandIds) {
      if (brandIds.length === 0) {
        return;
      }

      const updatedBrands = getSelectedPortfolioBrands(this.portfolioBrands, brandIds);

      await this.$store.dispatch('setPortfolioBrands', updatedBrands);
    },
    async runExplorerReport() {
      await this.getAndSetExplorerData();
      await this.createAndUpdateDashboardReport();
    },
    async getAndSetDashboardReport(dashboardReportId) {
      this.emitLoadingDashboardReport(true);

      let hasError = false;
      let errorMessage = '';

      try {
        const { dashboardReport } = await this.retrieveDashboardReport(dashboardReportId);

        await this.setDashboardReportFields(dashboardReport);

        // Since the request parameters are maintainted by the vuex store, we need to let Vue
        // finish those mutations so we can make the request with the correct parameters.
        this.$nextTick(() => {
          this.getAndSetExplorerData();
        });

        this.updateCurrentDashboardReportId(dashboardReportId);
      } catch (e) {
        hasError = true;
        errorMessage = getErrorMessage(e);
        this.emitError('permalinkRetrievalError');
      } finally {
        this.emitLoadingDashboardReport(false);
        eventDispatcher('dashboardReportLoaded', {
          ...this.analyticsObj,
          dashboard_report_id: dashboardReportId,
          loaded_by: this.currentUserEmail,
          has_error: hasError,
          error_message: errorMessage,
        });
      }
    },
    async getAndSetExplorerData() {
      if (!this.isValidReportRequest) {
        return;
      }

      this.isReportUpdated = false;
      this.explorerDataPending = true;
      this.dashboardError = false;
      let errorMessage = '';

      try {
        if (this.showNewExplorerMessages) this.setHint();
        const explorerData = await this.getExplorerData();

        if (explorerData?.rows?.length === 0) this.emitError('explorerRowsAreEmpty');

        this.setExplorerData(explorerData);
      } catch (e) {
        this.dashboardError = true;
        errorMessage = getErrorMessage(e);

        if (e?.status === 502 || e?.status === 504) {
          this.emitError('responseSizeTooLargeError', this.hint);
        } else {
          this.emitError('dashboardError');
        }

        if (this.explorerData) {
          this.$store.commit('updateExplorerData', undefined);
        }

      } finally {
        this.explorerDataPending = false;
        eventDispatcher('dashboardRequestResponse', {
          ...this.analyticsObj,
          has_error: this.dashboardError,
          error_message: errorMessage,
        });
      }
    },
    setHint() {
      const hintBase = 'Hint: Select up to ';

      if (this.brandIds?.length > 5) this.hint = hintBase + '5 brands.';
      else if (this.selectedDimensions?.length > 5) this.hint = hintBase + '5 dimensions.';
      else if (this.selectedMetrics?.length > 5) this.hint = hintBase + '5 metrics.';
      else if (getNumberOfDays(this.dateRange) > 180) this.hint = hintBase + '6 months of data.';
      else this.hint = '';
    },
    async setDashboardReportFields(dashboardReport) {
      if (!dashboardReport) {
        throw this.dashboardReportDoesNotExistErrorMessage;
      }

      const {
        view,
        hasTimeSeries,
        brandIds,
        startDate,
        endDate,
        dimensions,
        metrics,
        filters,
      } = dashboardReport;

      if (!view || view !== this.viewName || !brandIds) {
        throw this.dashboardReportRetrievalErrorMessage;
      }

      await this.updatePortfolioBrands(brandIds);

      if (hasTimeSeries !== undefined && hasTimeSeries !== this.showExplorerTimeSeriesChart) {
        this.$emit('showTimeSeriesUpdated', hasTimeSeries);
      }

      if (startDate) {
        const reportStartDate = getLocalDateFromUnixTimestamp(startDate);

        this.$store.commit('updateReportStartDate', reportStartDate);
      }

      if (endDate) {
        const reportEndDate = getLocalDateFromUnixTimestamp(endDate);

        this.$store.commit('updateReportEndDate', reportEndDate);
      }

      this.$store.commit('updateReportDimensions', this.arrayIfEmpty(dimensions));
      this.$store.commit(
        'updateReportMetrics',
        getNormalizedMetricValues(this.defaultMetrics, metrics),
      );
      this.$store.commit('updateExplorerAllowedFilters', explorerAllowedFilters());

      const cleanedFilters = this.arrayIfEmpty(filters).map(f => {
        if (f.value === undefined) {
          return { ...f, value: [null] };
        }

        if (Array.isArray(f.value) && f.value.some(v => v === undefined)) {
          return { ...f, value: f.value.map(v => v === undefined ? null : v) };
        }

        return f;
      });

      this.$store.dispatch(
        'setDashboardSelectedFilters',
        cleanedFilters,
      );
    },
    setExplorerData(data) {
      this.explorerDataPending = false;

      this.columnDisplayOrdering = this.getColumnDisplayOrdering(
        this.arrayIfEmpty(this.selectedDimensions),
        this.arrayIfEmpty(this.selectedMetrics),
      );

      this.$store.commit('updateExplorerData', data);

      this.updateYAxis(this.selectedMetrics[0]);
    },
    async retrieveDashboardReport(dashboardReportId) {
      if (!dashboardReportId) {
        return null;
      }

      eventDispatcher('dashboardReportRequested', {
        dashboard_view: this.viewName,
        dashboard_report_id: dashboardReportId,
        requested_by: this.currentUserEmail,
      });

      try {
        const token = await this.$auth.getTokenSilently();

        return await getDashboardReport(dashboardReportId, token);
      } catch (e) {
        throw getErrorMessage(e);
      }
    },
    async getExplorerData() {
      const explorerPayload = {
        brandIds: this.encodedBrandIds,
        dimensions: this.selectedDimensions,
        metrics: this.selectedMetrics,
        dateRange: {
          startDate: this.startDateStr,
          endDate: this.endDateStr,
          granularity: this.granularity,
        },
        unique: this.isSelectedReportUnique,
        filters: this.selectedFilters,
      };

      eventDispatcher('dashboardRequest', {
        ...this.analyticsObj,
      });

      const token = await this.$auth.getTokenSilently();
      const { data } = await getDashboardExplorerData(explorerPayload, token);

      return data;
    },
    async createAndUpdateDashboardReport() {
      if (!this.isValidReportRequest) {
        return;
      }

      let dashboardReportId;

      try {
        dashboardReportId = await this.createCurrentDashboardReport();
      } catch (e) {
        this.emitError('permalinkSaveError');

        return;
      }

      this.updateCurrentDashboardReportId(dashboardReportId);

      this.$router.push({
        name: 'explorer-id',
        params: { dashboardReportId },
      });
    },
    async createCurrentDashboardReport() {
      const dashboardReport = {
        view: this.viewName,
        hasTimeSeries: this.showExplorerTimeSeriesChart,
        startDate: this.startDateStr,
        endDate: this.endDateStr,
        brandIds: this.brandIds,
        dimensions: this.selectedDimensions,
        metrics: this.selectedMetrics,
        filters: this.selectedFilters,
      };

      let createdDashboardReportId;
      let hasError = false;
      let errorMessage = '';

      try {
        const token = await this.$auth.getTokenSilently();

        const { id } = await createDashboardReport(dashboardReport, token);

        createdDashboardReportId = id;

        return createdDashboardReportId;
      } catch (e) {
        hasError = true;
        errorMessage = getErrorMessage(e);

        throw errorMessage;
      } finally {
        eventDispatcher('dashboardReportCreated', {
          ...this.analyticsObj,
          dashboard_report_id: createdDashboardReportId,
          created_at: dashboardReport.createdAt,
          created_by: dashboardReport.createdBy,
          has_error: hasError,
          error_message: errorMessage,
        });
      }
    },
    getColumnDisplayOrdering(dimensionsValues, metricsValues) {
      const reportFieldValues = dimensionsValues.concat(metricsValues);

      return reportFieldValues
        .map(reportField => getReportFieldSnakeFromValue(this.allReportFields, reportField));
    },
    emitLoadingDashboardReport(isLoading) {
      this.$emit('loadingDashboardReport', isLoading);
    },
    emitError(type, hint) {
      this.$emit('showMessage', { type, hint });
      this.$router.replace({ name: 'explorer-base' });
    },
    clearCurrentDashboardReportId() {
      if (this.currentDashboardReportId) {
        this.updateCurrentDashboardReportId(null);
        this.$router.push({ name: 'explorer-base' });
      }

      this.isReportUpdated = true;
    },
    updateCurrentDashboardReportId(dashboardReportId) {
      this.$store.commit('updateCurrentDashboardReportId', dashboardReportId);
    },
    containsSameElements(arr1, arr2) {
      const s1 = arr1.slice().sort();
      const s2 = arr2.slice().sort();

      return s1.length === s2.length && s1.every((x, index) => x === s2[index]);
    },
    arrayIfEmpty(arr) {
      return arr ? arr : [];
    },
    setSortKey(columns) {
      let tempSortKey = '';

      if (columns.includes('page_views')) {
        tempSortKey = 'page_views';
      } else if (columns.includes('purchase_intent_rate')) {
        tempSortKey = 'purchase_intent_rate';
      } else if (this.initialSort !== '') {
        tempSortKey = this.initialSort;
      }

      this.sortKey = tempSortKey;
    },
  },
  watch: {
    $route(to, _from) {
      const dashboardReportId = to.params.dashboardReportId;

      if (dashboardReportId && dashboardReportId !== this.currentDashboardReportId) {
        this.getAndSetDashboardReport(dashboardReportId);
      }
    },
    tableColumns(newVal) {
      this.setSortKey(newVal);
    },
    selectedBrandIds(newIds, oldIds) {
      if (!this.showNewBrandSelector) return;

      if (!isEqual(newIds, oldIds)) {
        this.$emit('clearSelectedFilters');
        this.isReportUpdated = true;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.explorer-view {
  display: flex;
  flex-direction: column;
}

.row {
  display: flex;
  flex-direction: row;
  margin: 8px 0;
}

.filters-header {
  padding-top: 12px;
}

.explore-table {
  min-height: 450px;
  margin: 16px 0px 64px 0px;
  border-radius: 4px;
  position: relative;

  &.active {
    background-color: #fff;
    box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.4);
  }
}

div.explorer-chart {
  padding: 24px 0px;
  max-height: 506px;
  background-color: #fff;
  margin-top: 32px;
  border-radius: 8px;
  margin-bottom: 32px;
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.4);
}

.chart-buttons {
  padding: 0 24px;
  align-items: center;
  justify-content: space-between;
  div {
    margin-right: 70px;
  }
}

#time-series-metric-select {
  width: auto;
}

.time-series-chart {
  width: 100%;
  padding-bottom: 16px;
}

span.chart-title {
  font-size: 18px;
  margin-right: 12px;
}

.row.chart {
  justify-content: center;
  align-items: center;
  height: 83%;
}

.fade-container-enter-active,
.fade-container-leave-active {
  transition: opacity 0.25s;
}
.fade-container-enter,
.fade-container-leave-to {
  opacity: 0;
}

.table {
  height: auto;
}

.explorer {
  padding: 0px 4px;
}

.explorer-table-box {
  clear: both;
  padding: 20px 0px;
  min-height: 600px;
  height: 45vh;
  max-height: 80vh;
  overflow: hidden;

  // default vuetify z index is higher than slideout overlay
  ::v-deep
    .vuetify-styling
    .v-data-table--fixed-header>.v-data-table__wrapper>table>thead>tr>th {
    z-index: 1;
  }
}
.message-bar {
  width: calc(100% + 48px);
  margin-left: -24px;
}
</style>
